'use strict';

/**
 * Lavazza SweetAlert2 helpers
 */
const Swal = require('sweetalert2').default;
const drawer = require('../components/drawer');

/**
 * Builds options for the right-hand panel (38% width, full viewport height, close control top-right).
 * Merges `overrides` into defaults; `customClass` is shallow-merged per key (override value replaces the
 * default for that key—include base classes in the string when you need both).
 * Lifecycle hooks from `overrides` are composed with the shared drawer behavior.
 *
 * @param {import('sweetalert2').SweetAlertOptions} [overrides]
 * @returns {import('sweetalert2').SweetAlertOptions}
 */
function rightPanelOptions(overrides) {
	const defaults = {
		position: 'top-end',
		grow: 'column',
		width: '38%',
		padding: 0,
		showConfirmButton: false,
		showCancelButton: false,
		showCloseButton: true,
		closeButtonHtml: '<i class="icon-close sa-right-panel__close-icon" aria-hidden="true"></i>',
		showClass: {
			popup: 'sa2-fade-in-right sa2-anim-faster',
		},
		hideClass: {
			popup: 'sa2-fade-out-right sa2-anim-faster',
		},
		customClass: {
			container: 'sa-right-panel-container',
			popup: 'sa-right-panel-popup',
			htmlContainer: 'sa-right-panel-html',
		},
		didOpen: function () {
			if (drawer && typeof drawer.freezeStickyHeaders === 'function') {
				drawer.freezeStickyHeaders();
			}
			document.documentElement.classList.add('no-overflow');
			if (typeof window.$ !== 'undefined' && window.$) {
				window.$('body').trigger('swal:open');
			}
		},
		didClose: function () {
			document.documentElement.classList.remove('no-overflow');
			if (typeof window.$ !== 'undefined' && window.$) {
				window.$('body').trigger('swal:close');
			}
			if (drawer && typeof drawer.restoreStickyHeaders === 'function') {
				drawer.restoreStickyHeaders();
			}
		},
	};

	if (!overrides) {
		return defaults;
	}

	const overrideDidOpen = overrides.didOpen;
	const overrideDidClose = overrides.didClose;
	const merged = Object.assign({}, defaults, overrides);
	merged.customClass = Object.assign({}, defaults.customClass, overrides.customClass);
	merged.showClass = Object.assign({}, defaults.showClass, overrides.showClass);
	merged.hideClass = Object.assign({}, defaults.hideClass, overrides.hideClass);
	merged.didOpen = function didOpenWithSharedRightPanelBehavior() {
		defaults.didOpen.apply(this, arguments);
		if (typeof overrideDidOpen === 'function') {
			overrideDidOpen.apply(this, arguments);
		}
	};
	merged.didClose = function didCloseWithSharedRightPanelBehavior() {
		try {
			if (typeof overrideDidClose === 'function') {
				overrideDidClose.apply(this, arguments);
			}
		} finally {
			defaults.didClose.apply(this, arguments);
		}
	};
	return merged;
}

/**
 * Opens the right-hand panel via `Swal.fire(rightPanelOptions(options))`.
 *
 * @param {import('sweetalert2').SweetAlertOptions} options
 * @returns {Promise}
 */
function openRightPanel(options) {
	return Swal.fire(rightPanelOptions(options));
}

/**
 * Preset: `position` top-end (upper-right), fixed width 600px, column grow, fade/slide from the right.
 * No confirm or cancel buttons.
 */
const topEnd = Swal.mixin({
	showClass: {
		popup: 'sa2-fade-in-right sa2-anim-faster',
	},
	hideClass: {
		popup: 'sa2-fade-out-right sa2-anim-faster',
	},
	grow: 'column',
	width: 600,
	showConfirmButton: false,
	showCancelButton: false,
	position: 'top-end',
});

/**
 * Preset: centered modal (default SweetAlert2 placement), fade in/out, icon close button.
 * No confirm or cancel buttons.
 */
const center = Swal.mixin({
	showClass: {
		popup: 'sa2-fade-in sa2-anim-faster',
	},
	hideClass: {
		popup: 'sa2-fade-out sa2-anim-faster',
	},
	closeButtonHtml: '<i class="icon-close" aria-hidden="true"></i>',
	showCloseButton: true,
	showConfirmButton: false,
	showCancelButton: false,
});

/**
 * Preset: SweetAlert2 mixin from {@link rightPanelOptions} defaults (full-height right drawer, scrollable HTML).
 */
const rightPanel = Swal.mixin(rightPanelOptions());

/**
 * Builds options for toast notifications (top, auto-dismiss, hover pauses timer).
 * Merges `overrides` into defaults; `customClass` is shallow-merged per key.
 *
 * @param {import('sweetalert2').SweetAlertOptions} [overrides]
 * @returns {import('sweetalert2').SweetAlertOptions}
 */
function toastOptions(overrides) {
	const defaults = {
		toast: true,
		position: 'top',
		width: '100%',
		padding: 0,
		showConfirmButton: false,
		showCancelButton: false,
		showCloseButton: false,
		timer: 5000,
		timerProgressBar: false,
		showClass: {
			popup: 'sa2-fade-in sa2-anim-faster',
		},
		hideClass: {
			popup: 'sa2-fade-out sa2-anim-faster',
		},
		customClass: {
			container: 'sa-toast-container',
			popup: 'sa-toast-popup',
			htmlContainer: 'sa-toast-html',
		},
		didOpen: function (popup) {
			popup.addEventListener('mouseenter', Swal.stopTimer);
			popup.addEventListener('mouseleave', Swal.resumeTimer);
			if (typeof window.$ !== 'undefined' && window.$) {
				window.$('body').trigger('swal:open');
			}
		},
		didClose: function () {
			if (typeof window.$ !== 'undefined' && window.$) {
				window.$('body').trigger('swal:close');
			}
		},
	};

	if (!overrides) {
		return defaults;
	}

	const merged = Object.assign({}, defaults, overrides);
	merged.customClass = Object.assign({}, defaults.customClass, overrides.customClass);
	merged.showClass = Object.assign({}, defaults.showClass, overrides.showClass);
	merged.hideClass = Object.assign({}, defaults.hideClass, overrides.hideClass);
	return merged;
}

/**
 * Opens a toast via `Swal.fire(toastOptions(options))`.
 *
 * @param {import('sweetalert2').SweetAlertOptions} options
 * @returns {Promise}
 */
function openToast(options) {
	return Swal.fire(toastOptions(options));
}

/**
 * Preset: SweetAlert2 mixin from {@link toastOptions} defaults.
 */
const toast = Swal.mixin(toastOptions());

/**
 * Exports: `Swal` (default instance), `openRightPanel`, `rightPanelOptions`, `topEnd`, `center`, `rightPanel`,
 * `openToast`, `toastOptions`, `toast`.
 */
module.exports = {
	Swal: Swal,
	openRightPanel: openRightPanel,
	rightPanelOptions: rightPanelOptions,
	topEnd: topEnd,
	center: center,
	rightPanel: rightPanel,
	openToast: openToast,
	toastOptions: toastOptions,
	toast: toast,
};
